/** @jsx jsx */

import { get } from 'lodash';

import Icon from '@zapier/common-components/src/components/Icon';
import { marginToCss, Spacing } from '@zapier/common-utils';
import { Border, Colors, Typography } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import MessageList from './MessageList';
import MessageText from './MessageText';

export type MessageBarTypes =
  | 'default'
  | 'error'
  | 'info'
  | 'plain'
  | 'success'
  | 'teams'
  | 'warning';

type BorderRadiusTypes = keyof typeof Border.radius;

type TypographyTypes = keyof typeof Typography;

const TYPES_MAP = {
  default: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.storm,
      borderColor: Colors.batmobile,
    },
  },

  error: {
    defaultTitle: 'Bargle!',
    icon: 'error',
    styles: {
      backgroundColor: Colors.status.error.bg,
      borderColor: Colors.status.error.border,
    },
  },

  info: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.iceman,
      borderColor: Colors.captainAmerica,
    },
  },

  plain: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.status.plain.bg,
      borderColor: Colors.status.plain.border,
    },
  },

  success: {
    defaultTitle: 'Success!',
    icon: 'check',
    styles: {
      backgroundColor: Colors.status.success.bg,
      borderColor: Colors.status.success.border,
    },
  },

  teams: {
    defaultTitle: 'Team!',
    icon: 'team',
    iconStyle: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      fill: Colors.mystique,
    },
    styles: {
      backgroundColor: Colors.thunder,
      borderColor: Colors.mystique,
    },
  },

  warning: {
    defaultTitle: 'Warning.',
    icon: 'alert',
    styles: {
      backgroundColor: Colors.status.warning.bg,
      borderColor: Colors.status.warning.border,
    },
  },
};

const messageBarBaseCss = (
  borderRadius: BorderRadiusTypes,
  hasBorder: boolean,
  margin: Spacing | undefined | null,
  fitToScreen: boolean
) =>
  css([
    {
      ...marginToCss(margin, '20px'),
      borderRadius: Border.radius[borderRadius] || Border.radius.default,
      color: Colors.deepSpace,
      display: 'flex',
      fontSize: 16,
      maxWidth: fitToScreen ? '90vw' : 'unset',
      padding: 15,
      position: 'relative',
      transition: 'background-color 300ms',
      '& a': {
        color: 'inherit',
      },
    },
    hasBorder && {
      borderStyle: 'solid',
      borderWidth: Border.size.default,
    },
  ]);

const messageCss = css({
  flex: 1,
  lineHeight: 1.6,
  minWidth: 0,
});

const iconBaseCss = css({
  backgroundColor: Colors.white,
  borderRadius: '50%',
  marginRight: 8,
  marginTop: 3,
});

const dismissCss = css({
  marginLeft: 8,
  marginTop: 3,
});

const titleCss = (titleStyle?: TypographyTypes) => [
  css({
    fontWeight: 600,
  }),
  titleStyle && Typography[titleStyle],
];

type Props = {
  /** The border radius. Accepts any of the keys from `Border.radius` from `@zapier/theme`. */
  borderRadius?: BorderRadiusTypes;
  /** Children (usually text) to display as the main content of the message. */
  children?: React.ReactNode;
  /** Prevents an icon from being rendered. */
  hideIcon?: boolean;
  /** Render borders or not. */
  hasBorder?: boolean;
  /** Icon name (will override the default icon for the type) */
  icon?: string;
  /** Does not allow the bar to overflow screen on mobile (for toasts) */
  fitToScreen?: boolean;
  /** Handler for dismissing the message. */
  onDismiss?: () => void;
  margin?: Spacing;
  /** Bolded title to display in the header. */
  title?: string;
  /**
   * The type (effectively the "style") of title to render. One of a list of
   * preset types, which correspond exactly to the Zapier Visual Identity:
   * https://github.com/zapier/zapier-ui/issues/36.
   */
  titleStyle?: keyof typeof Typography;
  /** The type of message bar to display. */
  type: MessageBarTypes;
  /**
   * The type (effectively the "style") of text to render. One of a list of
   * preset types, which correspond exactly to the Zapier Visual Identity:
   * https://github.com/zapier/zapier-ui/issues/36. These styles are mixed
   * in to the MessageBar styles, overriding any existing MessageBar styles
   * specified by the MessageBar type.
   */
  typography?: keyof typeof Typography; // Use the TypographyName type from @zapier/theme instead after our version of TypeScript supports the `import { type TypographyName }` syntax.
};

/**
 * Displays a bar with useful information for the user.
 *
 * Comes bundled with sub-components:
 * - `<MessageBar.MessageText>`: Used for displaying additional sub-text.
 * - `<MessageBar.MessageList>`: Used with lists.
 */
const MessageBar = (props: Props) => {
  const type = TYPES_MAP[props.type];
  const typographyStyles = props.typography ? Typography[props.typography] : {};

  const messageBarCss = css(
    messageBarBaseCss(
      props.borderRadius!,
      props.hasBorder!,
      props.margin,
      props.fitToScreen!
    ),
    type.styles,
    typographyStyles
  );

  const iconCss = css(
    iconBaseCss,
    {
      color: type.styles.borderColor,
    },
    get(type, 'iconStyle', {})
  );

  // Allow user to pass title (or empty title to clear) or fall back to default title for type
  const icon = !props.hideIcon && (props.icon || type.icon);

  const title = props.title !== undefined ? props.title : type.defaultTitle;

  return (
    <div css={messageBarCss}>
      {!!icon && <Icon css={iconCss} name={icon} size={20} />}

      <div css={messageCss}>
        {title && <strong css={titleCss(props.titleStyle)}>{title}</strong>}{' '}
        {props.children}
      </div>

      {!!props.onDismiss && (
        <Icon css={dismissCss} name="delete" onClick={props.onDismiss} />
      )}
    </div>
  );
};

MessageBar.defaultProps = {
  borderRadius: 'default',
  hasBorder: true,
  fitToScreen: false,
  hideIcon: false,
  type: 'default',
  titleStyle: 'section-head',
};

MessageBar.MessageList = MessageList;
MessageBar.MessageText = MessageText;

export default MessageBar;
